import React, { useState } from 'react';
import { Ellipsis, Expand, ArrowUp, ArrowDown, Trash2, FileUp, ArrowUp01 } from 'lucide-react';

import { Button } from '~/shadcn/components/ui/Button';
import { Checkbox } from '~/shadcn/components/ui/Checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '~/shadcn/components/ui/DropdownMenu';
import { Skeleton } from '~/shadcn/components/ui/Skeleton';

import { Combobox } from '~/shadcn/components/ui/Combobox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/shadcn/components/ui/Dialog';
import { Input } from '~/shadcn/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from '~/shadcn/components/ui/Table';
import { cn } from '~/shadcn/lib/utils';

import TablePageControls from '../../components/TablePageControls';
import TableSortDropdown from '../../components/TableSortDropdown';
import useFilteredAndSortedRows from '../../utils/useFilteredAndSortedRows';
import usePagination from '../../utils/usePagination';

import UploadProductCSVDialog from './UploadProductCSVDialog';
/**
 * Data table for displaying and managing product lists including sorting, filtering, and pagination.
 *
 * @param {Object} props
 * @param {string} props.carouselID - ID of the carousel
 * @param {string} props.dialogTitle - Title for the expanded dialog view
 * @param {boolean} props.loading - Loading state of the table
 * @param {Function} props.onAddRow - Function to handle adding a new row
 * @param {Function} props.onChange - Function to handle row changes
 * @param {Function} props.onMoveRowDown - Function to move a row down
 * @param {Function} props.onMoveRowUp - Function to move a row up
 * @param {Function} props.onMoveRowToIndex - Function to move a row to a specified index
 * @param {Function} props.onRemoveAllRows - Function to remove all rows
 * @param {Function} props.onRemoveRow - Function to remove a single row
 * @param {Function} props.onRemoveSelectedRows - Function to remove selected rows
 * @param {Function} [props.onUploadProductCSV] - Function to handle uploading a product CSV
 * @param {Array} props.options - Array of available options for the combobox
 * @param {Array} props.rows - Array of row data
 * @param {boolean} props.isDialog - Whether the table is rendered in a dialog
 * @param {string} props.initialRowsPerPage - Initial number of rows per page
 * @returns {JSX.Element} ProductTable component
 */
function ProductTable({
    carouselID,
    dialogTitle,
    loading,
    onAddRow,
    onChange,
    onMoveRowDown,
    onMoveRowUp,
    onMoveRowToIndex,
    onRemoveAllRows,
    onRemoveRow,
    onRemoveSelectedRows,
    onUploadProductCSV,
    options,
    rows,
    isDialog = false,
    initialRowsPerPage = '5',
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortState, setSortState] = useState({ key: null, direction: null });
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAssignRankDialog, setShowAssignRankDialog] = useState(null);
    const [showUploadProductCSVDialog, setShowUploadProductCSVDialog] = useState(false);

    const filteredAndSortedRows = useFilteredAndSortedRows(rows, searchQuery, sortState);
    const { currentRows, totalPages } = usePagination(filteredAndSortedRows, rowsPerPage, currentPage);

    const handleSelectAll = () => {
        if (selectedRows.size === currentRows.length) {
            setSelectedRows(new Set());
        } else {
            const updatedSelectedRows = new Set(selectedRows);
            currentRows.forEach((item) => {
                updatedSelectedRows.add(item.id);
            });
            setSelectedRows(updatedSelectedRows);
        }
    };

    const handleSelectRow = (itemId) => {
        setSelectedRows((prev) => {
            const updatedSelectedRows = new Set(prev);
            if (updatedSelectedRows.has(itemId)) {
                updatedSelectedRows.delete(itemId);
            } else {
                updatedSelectedRows.add(itemId);
            }
            return updatedSelectedRows;
        });
    };

    const handleSort = (key, direction) => {
        setSortState({ key, direction });
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
    };

    const handleRowsPerPageChange = (value) => {
        setRowsPerPage(value);
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleRemoveSelectedRows = () => {
        onRemoveSelectedRows([...selectedRows]);
        setSelectedRows(new Set());
    };

    const handleRemoveInvalidRows = () => {
        const invalidRows = rows.filter((item) => !item.valid).map((item) => item.id);
        onRemoveSelectedRows(invalidRows);
    };

    const handleUploadProductCSV = async (file) => {
        await onUploadProductCSV(file);
        setShowUploadProductCSVDialog(false);
    };

    const handleMoveRowToIndex = (index) => {
        onMoveRowToIndex(showAssignRankDialog, index);
        setShowAssignRankDialog(null);
    };

    // Calculate expanded rows per page for dialog mode
    const expandedRowsPerPage = rowsPerPage === 'all' ? 'all' : Math.max(parseInt(rowsPerPage, 10) || 5, 20).toString();

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex gap-2">
                <Input type="text" placeholder="Search items..." value={searchQuery} onChange={handleSearch} />
                <Combobox
                    align="end"
                    closeOnSelect={false}
                    options={options}
                    placeholder="Add row"
                    value=""
                    handleSelect={onAddRow}
                    hideCheckmark
                    popoverWidth="custom"
                    className="min-w-150"
                    disabled={loading}
                    commandProps={{
                        filter: (value, search) => value.toLowerCase().includes(search.toLowerCase()),
                    }}
                />
            </div>
            <div className={cn('border rounded-md overflow-auto', isDialog && 'flex-1 flex flex-col min-h-0')}>
                <div className={cn('relative', isDialog && 'overflow-auto flex-1')}>
                    <Table className="overflow-x-visible">
                        <TableHeader className="bg-muted z-10">
                            <TableRow className="[&_th]:text-muted-foreground [&_th]:text-xs [&_th]:font-normal hover:bg-muted [&_th]:bg-muted [&_th]:z-40 [&_th]:top-0 [&_th]:sticky [&_th]:before:content-[''] [&_th]:before:absolute [&_th]:before:left-0 [&_th]:before:bottom-0 [&_th]:before:w-full [&_th]:before:h-full [&_th]:before:border-b [&_th]:before:border-border border-none [&_th]:before:z-[-1]">
                                <TableHead className="min-w-[40px]">
                                    <Checkbox
                                        checked={currentRows.length > 0 && selectedRows.size === currentRows.length}
                                        onCheckedChange={handleSelectAll}
                                        className="bg-background"
                                    />
                                </TableHead>
                                <TableHead className="min-w-[50px]">
                                    <TableSortDropdown
                                        label="Rank"
                                        sortKey="rank"
                                        currentSortConfig={sortState}
                                        onSort={handleSort}
                                    />
                                </TableHead>
                                <TableHead className="min-w-[80px]">
                                    <TableSortDropdown
                                        label="External SKU"
                                        sortKey="externalSku"
                                        currentSortConfig={sortState}
                                        onSort={handleSort}
                                    />
                                </TableHead>
                                <TableHead className="w-[100%]">
                                    <TableSortDropdown
                                        label="Name"
                                        sortKey="name"
                                        currentSortConfig={sortState}
                                        onSort={handleSort}
                                    />
                                </TableHead>
                                <TableHead className="min-w-[40px]">
                                    <TableSortDropdown
                                        label="Valid"
                                        sortKey="valid"
                                        currentSortConfig={sortState}
                                        onSort={handleSort}
                                    />
                                </TableHead>
                                <TableHead className="min-w-[40px]">
                                    <TableSortDropdown
                                        label="Sold Out"
                                        sortKey="soldOut"
                                        currentSortConfig={sortState}
                                        onSort={handleSort}
                                    />
                                </TableHead>
                                <TableHead className="min-w-[50px] z-[500] right-[-1px] after:absolute after:top-0 after:left-0 after:w-full after:h-full after:border-l after:border-border after:z-[-1]">
                                    <TableHeadOptionsDropdown
                                        carouselID={carouselID}
                                        disabled={loading}
                                        isDialog={isDialog}
                                        onExpandTable={() => setIsExpanded(true)}
                                        onRemoveInvalidRows={handleRemoveInvalidRows}
                                        onRemoveSelectedRows={handleRemoveSelectedRows}
                                        onRemoveAllRows={onRemoveAllRows}
                                        onUploadProductCSV={
                                            typeof onUploadProductCSV === 'function'
                                                ? () => setShowUploadProductCSVDialog(true)
                                                : undefined
                                        }
                                        options={options}
                                        rows={rows}
                                        selectedRows={selectedRows}
                                    />
                                </TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {loading && <RowsLoadingSkeleton />}
                            {!loading && currentRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan="6" className="text-center py-4 text-muted-foreground">
                                        No items found
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading &&
                                currentRows.map((row) => {
                                    const externalSku = row.externalSku || row.external_sku;
                                    const moveRowUpDisabled = sortState.key !== null || rows[0].id === row.id;
                                    const moveRowDownDisabled =
                                        sortState.key !== null || rows[rows.length - 1].id === row.id;

                                    const selected = selectedRows.has(row.id);

                                    return (
                                        <TableRow
                                            key={row.id}
                                            data-state={selected ? 'selected' : 'notSelected'}
                                            className="group"
                                        >
                                            <TableCell>
                                                <Checkbox
                                                    checked={selected}
                                                    onCheckedChange={() => handleSelectRow(row.id)}
                                                />
                                            </TableCell>
                                            <TableCell className="text-center text-muted-foreground whitespace-nowrap">
                                                <p className="-ml-3">{row.rank}</p>
                                            </TableCell>
                                            <TableCell>{externalSku}</TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>
                                                <div className="h-full flex items-center justify-center">
                                                    {row.valid ? (
                                                        <div className="size-1.5 -ml-3 rounded-full bg-green-500" />
                                                    ) : (
                                                        <div className="size-1.5 -ml-3 rounded-full bg-red-500" />
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {row.soldOut ? 'True' : 'False'}
                                            </TableCell>
                                            <TableCell className="sticky right-[-1px] bg-background before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:border-l before:border-border before:z-[-1] group-hover:bg-[#f9f9f9] transition-colors group-data-[state=selected]:bg-muted">
                                                <TableCellOptionsDropdown
                                                    moveRowUpDisabled={moveRowUpDisabled}
                                                    moveRowDownDisabled={moveRowDownDisabled}
                                                    onAssignRank={() => setShowAssignRankDialog(externalSku)}
                                                    onMoveRowUp={() => onMoveRowUp(externalSku)}
                                                    onMoveRowDown={() => onMoveRowDown(externalSku)}
                                                    onRemove={() => onRemoveRow(externalSku)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <TablePageControls
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                selectedRows={selectedRows.size}
                totalRows={rows.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
            {isExpanded && (
                <ProductTableDialog
                    dialogTitle={dialogTitle}
                    loading={loading}
                    onAddRow={onAddRow}
                    onChange={onChange}
                    onDialogClose={() => setIsExpanded(false)}
                    onMoveRowDown={onMoveRowDown}
                    onMoveRowUp={onMoveRowUp}
                    onRemoveAllRows={onRemoveAllRows}
                    onRemoveSelectedRows={onRemoveSelectedRows}
                    options={options}
                    rows={rows}
                    initialRowsPerPage={expandedRowsPerPage}
                />
            )}
            <AssignRankDialog
                externalSku={showAssignRankDialog}
                isOpen={!!showAssignRankDialog}
                max={rows.length}
                min={1}
                onClose={() => setShowAssignRankDialog(false)}
                onSubmit={handleMoveRowToIndex}
            />
            <UploadProductCSVDialog
                carouselID={carouselID}
                isOpen={showUploadProductCSVDialog}
                onClose={() => setShowUploadProductCSVDialog(false)}
                onConfirm={handleUploadProductCSV}
            />
        </div>
    );
}

/**
 * Dropdown options menu for table headers.
 *
 * @param {Object} props
 * @param {string} props.carouselID - ID of the carousel
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {boolean} props.isDialog - Whether the table is rendered in a dialog
 * @param {Function} props.onExpandTable - Function to handle table expansion
 * @param {Function} props.onRemoveAllRows - Function to remove all rows
 * @param {Function} props.onRemoveInvalidRows - Function to remove invalid rows
 * @param {Function} props.onRemoveSelectedRows - Function to remove selected rows
 * @param {Function} [props.onUploadProductCSV] - Function to upload a product CSV
 * @param {Array} props.rows - Array of row data
 * @param {Set} props.selectedRows - Set of selected row IDs
 * @returns {JSX.Element} TableHeadOptionsDropdown component
 */
function TableHeadOptionsDropdown({
    carouselID,
    disabled,
    isDialog,
    onExpandTable,
    onRemoveAllRows,
    onRemoveInvalidRows,
    onRemoveSelectedRows,
    onUploadProductCSV,
    rows,
    selectedRows,
}) {
    const invalidRows = rows.filter((row) => !row.valid);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    className="data-[state=open]:bg-primary/5 hover:bg-primary/5 hover:[&_svg]:text-accent-foreground"
                    disabled={disabled}
                    variant="ghost"
                    size="iconSm"
                    type="button"
                >
                    <Ellipsis className="text-muted-foreground" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {!isDialog && (
                    <DropdownMenuItem onClick={onExpandTable}>
                        <Expand className="h-4 w-4 stroke-1" /> Expand
                    </DropdownMenuItem>
                )}
                {typeof onUploadProductCSV === 'function' && (
                    <DropdownMenuItem onClick={onUploadProductCSV} disabled={!carouselID}>
                        <FileUp />
                        Upload Product CSV
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={onRemoveInvalidRows} disabled={invalidRows.length < 1}>
                    <Trash2 className="h-4 w-4 stroke-1" /> Remove invalid ({invalidRows.length})
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    variant="destructive"
                    onClick={onRemoveSelectedRows}
                    disabled={selectedRows?.size === 0}
                >
                    <Trash2 className="h-4 w-4 stroke-1" /> Remove selected
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={onRemoveAllRows} disabled={rows.length === 0}>
                    <Trash2 className="h-4 w-4 stroke-1" /> Remove all
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Dropdown menu for individual table row options
 *
 * @param {Object} props
 * @param {boolean} props.moveRowUpDisabled - Whether moving row up is disabled
 * @param {boolean} props.moveRowDownDisabled - Whether moving row down is disabled
 * @param {Function} props.onAssignRank - Function to open the assign rank dialog
 * @param {Function} props.onMoveRowUp - Function to move row up
 * @param {Function} props.onMoveRowDown - Function to move row down
 * @param {Function} props.onRemove - Function to remove row
 * @returns {JSX.Element} TableCellOptionsDropdown component
 */
function TableCellOptionsDropdown({
    moveRowUpDisabled,
    moveRowDownDisabled,
    onAssignRank,
    onMoveRowUp,
    onMoveRowDown,
    onRemove,
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    className="data-[state=open]:bg-primary/5 hover:bg-primary/5 hover:[&_svg]:text-accent-foreground"
                    variant="ghost"
                    size="iconSm"
                    type="button"
                >
                    <Ellipsis className="text-muted-foreground" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onAssignRank}>
                    <ArrowUp01 className="h-4 w-4 stroke-1" /> Assign rank
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={moveRowUpDisabled} onClick={onMoveRowUp}>
                    <ArrowUp className="h-4 w-4 stroke-1" /> Move up
                </DropdownMenuItem>
                <DropdownMenuItem disabled={moveRowDownDisabled} onClick={onMoveRowDown}>
                    <ArrowDown className="h-4 w-4 stroke-1" /> Move down
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={onRemove}>
                    <Trash2 className="h-4 w-4 stroke-1" /> Remove
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Dialog view of the product table, allowing a larger view of the table
 *
 * @param {Object} props
 * @param {string} props.dialogTitle - Title for the dialog
 * @param {boolean} props.loading - Loading state of the table
 * @param {Function} props.onAddRow - Function to handle adding a new row
 * @param {Function} props.onChange - Function to handle row changes
 * @param {Function} props.onDialogClose - Function to handle dialog close
 * @param {Function} props.onMoveRowDown - Function to move a row down
 * @param {Function} props.onMoveRowUp - Function to move a row up
 * @param {Function} props.onRemoveAllRows - Function to remove all rows
 * @param {Function} props.onRemoveSelectedRows - Function to remove selected rows
 * @param {Array} props.options - Array of available options
 * @param {Array} props.rows - Array of row data
 * @param {string} props.initialRowsPerPage - Initial number of rows per page
 * @returns {JSX.Element} ProductTableDialog component
 */
function ProductTableDialog({
    dialogTitle,
    loading,
    onAddRow,
    onChange,
    onDialogClose,
    onMoveRowDown,
    onMoveRowUp,
    onRemoveAllRows,
    onRemoveSelectedRows,
    options,
    rows,
    initialRowsPerPage = '5',
}) {
    return (
        <Dialog open onOpenChange={onDialogClose}>
            <DialogContent
                className="sm:max-w-[calc(100%-4rem)] max-h-[calc(100%-4rem)] overflow-hidden flex flex-col h-full"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {dialogTitle && (
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                )}
                <div className={cn('flex-1 flex flex-col min-h-0', !dialogTitle && 'pt-6')}>
                    <ProductTable
                        loading={loading}
                        onAddRow={onAddRow}
                        onChange={onChange}
                        onMoveRowDown={onMoveRowDown}
                        onMoveRowUp={onMoveRowUp}
                        onRemoveAllRows={onRemoveAllRows}
                        onRemoveSelectedRows={onRemoveSelectedRows}
                        options={options}
                        rows={rows}
                        isDialog
                        initialRowsPerPage={initialRowsPerPage}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Dialog component for assigning a rank to an external SKU.
 *
 * @param {Object} props - The component props
 * @param {string} props.externalSku - The external SKU identifier to display in the dialog
 * @param {boolean} props.isOpen - Controls whether the dialog is open or closed
 * @param {number} props.max - The maximum allowed rank value
 * @param {number} props.min - The minimum allowed rank value
 * @param {Function} props.onClose - Callback function called when the dialog is closed
 * @param {Function} props.onSubmit - Callback function called when rank is submitted, receives the rank value (0-indexed)
 * @returns {JSX.Element} A dialog with a number input for rank assignment
 */
function AssignRankDialog({ externalSku, isOpen, max, min, onClose, onSubmit }) {
    const [rank, setRank] = useState(1);

    const handleSubmit = () => {
        onSubmit(rank - 1);
        setRank(1);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Assign Rank</DialogTitle>
                    <DialogDescription>
                        Assign a rank for external SKU <span className="font-medium">{externalSku}</span>.
                    </DialogDescription>
                </DialogHeader>
                <form id="assign-rank-form" onSubmit={handleSubmit}>
                    <Input type="number" min={min} max={max} onChange={(e) => setRank(e.target.value)} value={rank} />
                </form>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button form="assign-rank-form" type="submit" variant="default">
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Loading skeleton for product rows
 *
 * @returns {JSX.Element} RowsLoadingSkeleton component
 */
function RowsLoadingSkeleton() {
    return Array.from({ length: 3 }).map((_, index) => (
        <TableRow key={index}>
            <TableCell>
                <Skeleton className="w-full h-5" />
            </TableCell>
            <TableCell>
                <Skeleton className="w-3/4 h-5" />
            </TableCell>
            <TableCell>
                <Skeleton className="w-3/4 h-5" />
            </TableCell>
            <TableCell>
                <Skeleton
                    className={cn('h-5', index === 0 && 'w-3/4', index === 1 && 'w-full', index === 2 && 'w-1/2')}
                />
            </TableCell>
        </TableRow>
    ));
}

export default ProductTable;
